import type { FrameworkArgument } from '../framework/framework.d';
import { isFrameworkArgOptionalNoneDefault, isFrameworkIntegerArgType } from './frameworkArgTypes';

/**
 * Dialog state convention for tuple arguments.
 *
 * Tuple values are stored flat in the dialog's `argValues: Record<string,string>`
 * map using dotted keys: e.g. for an argument named `color_at_min_input` with a
 * `red` member, the key is `color_at_min_input.red`. Non-tuple arguments
 * continue to use their plain `argName` key.
 *
 * Callers should use the helpers here so the convention stays consistent across
 * the dialog, hydration, validation, dirty-state diff and binding builder.
 */

/** Whether an argument is a tuple shape (case-insensitive). */
export function isTupleArg(arg: FrameworkArgument | { type?: string } | undefined): boolean {
  return String(arg?.type || '').toLowerCase() === 'tuple';
}

/**
 * Whether an argument is a sequence-of-tuples shape, e.g. `Sequence[tuple[int, str]]`.
 * Such an argument carries `members` describing the shape of a single row.
 */
export function isSequenceTupleArg(arg: FrameworkArgument | { type?: string } | undefined): boolean {
  return /^sequence\[\s*tuple\[/i.test(String(arg?.type || '').trim());
}

/**
 * Compound `argValues` key for a single member of one row in a sequence-of-tuples
 * argument, e.g. for `lines` row 0 member `text`: `lines.0.text`. The row index
 * keeps multiple tuple rows distinct in the flat `argValues` map.
 */
export function sequenceTupleMemberValueKey(argName: string, rowIndex: number, memberName: string): string {
  return `${argName}.${rowIndex}.${memberName}`;
}

/**
 * Ordered, de-duplicated row indices present in `argValues` for a sequence-of-tuples
 * argument named `argName` (parsed from keys shaped `<argName>.<rowIndex>.<member>`).
 */
export function sequenceTupleRowIndices(argName: string, argValues: Record<string, string>): number[] {
  const prefix = `${argName}.`;
  const indices = new Set<number>();
  for (const key of Object.keys(argValues || {})) {
    if (!key.startsWith(prefix)) continue;
    const rest = key.slice(prefix.length);
    const dot = rest.indexOf('.');
    if (dot <= 0) continue;
    const idx = Number(rest.slice(0, dot));
    if (Number.isInteger(idx) && idx >= 0) indices.add(idx);
  }
  return Array.from(indices).sort((a, b) => a - b);
}

/**
 * Builds ordered row objects (member name -> raw string) for a sequence-of-tuples
 * argument from a flat `argValues` map. Missing members default to an empty string.
 */
export function sequenceTupleRowsFromArgValues(seqArg: FrameworkArgument, argValues: Record<string, string>): Array<Record<string, string>> {
  const members = seqArg.members || [];
  return sequenceTupleRowIndices(seqArg.name, argValues).map((rowIndex) => {
    const row: Record<string, string> = {};
    for (const member of members) {
      row[member.name] = argValues[sequenceTupleMemberValueKey(seqArg.name, rowIndex, member.name)] ?? '';
    }
    return row;
  });
}

/** Reads the stored rows for a sequence-of-tuples argument from `arg.value` (an array), or `[]`. */
export function readSequenceTupleRows(seqArg: FrameworkArgument): unknown[] {
  return Array.isArray(seqArg.value) ? seqArg.value : [];
}

/**
 * Flattens parsed rows into indexed `argValues` keys for a sequence-of-tuples argument.
 * Each row may be a positional array (`[1, "Ready"]`) or a by-name object; member values
 * are coerced to strings for the flat dialog map.
 */
export function sequenceTupleRowsToArgValues(seqArg: FrameworkArgument, rows: unknown[]): Record<string, string> {
  const members = seqArg.members || [];
  const out: Record<string, string> = {};
  rows.forEach((row, rowIndex) => {
    members.forEach((member, memberIndex) => {
      const value = readSequenceTupleMemberValue(row, memberIndex, member.name);
      out[sequenceTupleMemberValueKey(seqArg.name, rowIndex, member.name)] = value === undefined || value === null ? '' : String(value);
    });
  });
  return out;
}

/**
 * The "key" member of a sequence-of-tuples argument: the first bounded integer member
 * (integer type with numeric `min` and `max`), e.g. `line_number` (1–7) on
 * `print_to_screen_lines_action`. Used to cap the row count and enforce unique values.
 * Returns `undefined` when no member qualifies (then rows are unbounded and not deduped).
 */
export function sequenceTupleKeyMember(seqArg: FrameworkArgument): FrameworkArgument | undefined {
  return (seqArg.members || []).find(
    (member) => isFrameworkIntegerArgType(String(member.type || '')) && typeof member.min === 'number' && typeof member.max === 'number'
  );
}

/**
 * Canonical form of a sequence-of-tuples key value for uniqueness comparison. For a bounded-integer
 * key member, equivalent spellings (`1`, `01`, `1.0`) collapse to the same canonical string so they
 * count as the same target row; non-numeric / unbounded keys pass through trimmed. Shared by the save
 * validation, the inline duplicate feedback, and Add-row seeding so all three agree.
 */
export function canonicalizeSequenceTupleKeyValue(keyMember: FrameworkArgument | undefined, rawValue: string): string {
  const trimmed = String(rawValue ?? '').trim();
  const isBoundedInt = !!keyMember && typeof keyMember.min === 'number' && typeof keyMember.max === 'number';
  return isBoundedInt && trimmed !== '' && Number.isFinite(Number(trimmed)) ? String(Number(trimmed)) : trimmed;
}

/** Maximum distinct rows allowed = the key member's inclusive range size, or `undefined` when unbounded. */
export function sequenceTupleMaxRows(seqArg: FrameworkArgument): number | undefined {
  const keyMember = sequenceTupleKeyMember(seqArg);
  if (!keyMember || typeof keyMember.min !== 'number' || typeof keyMember.max !== 'number') return undefined;
  return Math.max(0, Math.floor(keyMember.max) - Math.ceil(keyMember.min) + 1);
}

/**
 * Indexed `argValues` for a single default row of a sequence-of-tuples argument (member
 * `defaultValue` when present, otherwise an empty string). When the argument has a bounded
 * integer key member, that member is seeded to `usedKeyValues`-aware next free value in range
 * (falling back to the member default) so freshly added rows don't collide.
 */
export function defaultSequenceTupleRowArgValues(
  seqArg: FrameworkArgument,
  rowIndex = 0,
  usedKeyValues: ReadonlySet<string> = new Set()
): Record<string, string> {
  const keyMember = sequenceTupleKeyMember(seqArg);
  const out: Record<string, string> = {};
  for (const member of seqArg.members || []) {
    let value = member.defaultValue === undefined || member.defaultValue === null ? '' : String(member.defaultValue);
    if (keyMember && member.name === keyMember.name && typeof keyMember.min === 'number' && typeof keyMember.max === 'number') {
      const free = nextFreeIntInRange(keyMember.min, keyMember.max, usedKeyValues);
      if (free !== undefined) value = String(free);
    }
    out[sequenceTupleMemberValueKey(seqArg.name, rowIndex, member.name)] = value;
  }
  return out;
}

/** Smallest integer in [min, max] whose string form isn't in `used`, or `undefined` when the range is full. */
function nextFreeIntInRange(min: number, max: number, used: ReadonlySet<string>): number | undefined {
  for (let candidate = Math.ceil(min); candidate <= Math.floor(max); candidate++) {
    if (!used.has(String(candidate))) return candidate;
  }
  return undefined;
}

/**
 * Builds the by-name row objects for a sequence-of-tuples argument's stored `value` from a
 * flat `argValues` map (inverse of {@link sequenceTupleRowsToArgValues}). Mirrors
 * {@link tupleValueFromArgValues} for a single tuple, repeated per row.
 */
export function sequenceTupleArgValueFromArgValues(
  seqArg: FrameworkArgument,
  argValues: Record<string, string>,
  coerce: (member: FrameworkArgument, raw: string) => string | number = (_member, r) => r
): Array<Record<string, string | number | null>> {
  const members = seqArg.members || [];
  return sequenceTupleRowIndices(seqArg.name, argValues).map((rowIndex) => {
    const row: Record<string, string | number | null> = {};
    for (const member of members) {
      const raw = argValues[sequenceTupleMemberValueKey(seqArg.name, rowIndex, member.name)] ?? '';
      if (raw === '' && isFrameworkArgOptionalNoneDefault(member)) {
        row[member.name] = null;
      } else {
        row[member.name] = coerce(member, raw);
      }
    }
    return row;
  });
}

/**
 * Reads one member of a single stored sequence-of-tuples row, accepting either a
 * positional array (`[1, "Ready"]`) or a by-name object (`{ line_number: 1, text: "Ready" }`).
 * Returns `undefined` when the member isn't present.
 */
export function readSequenceTupleMemberValue(row: unknown, memberIndex: number, memberName: string): unknown {
  if (Array.isArray(row)) return row[memberIndex];
  if (row && typeof row === 'object') {
    const named = (row as Record<string, unknown>)[memberName];
    if (named !== undefined) return named;
  }
  return undefined;
}

/** Compound `argValues` key for a single member inside a tuple argument. */
export function tupleMemberValueKey(tupleName: string, memberName: string): string {
  return `${tupleName}.${memberName}`;
}

/** Builds a by-name object value for a tuple arg from a flat `argValues` map. */
export function tupleValueFromArgValues(
  tupleArg: FrameworkArgument,
  argValues: Record<string, string>,
  coerce: (member: FrameworkArgument, raw: string) => string | number = (_member, r) => r
): Record<string, string | number | null> {
  const out: Record<string, string | number | null> = {};
  for (const member of tupleArg.members || []) {
    const raw = argValues[tupleMemberValueKey(tupleArg.name, member.name)] ?? '';
    if (raw === '' && isFrameworkArgOptionalNoneDefault(member)) {
      out[member.name] = null;
    } else {
      out[member.name] = coerce(member, raw);
    }
  }
  return out;
}

/**
 * Reads the stored value for a single tuple member from the binding's `arg.value`,
 * accepting either a by-name object (preferred) or a positional array. Returns
 * `undefined` if the member isn't present.
 */
export function readTupleMemberValue(tupleArg: FrameworkArgument, memberIndex: number, memberName: string): unknown {
  const stored = tupleArg.value;
  if (Array.isArray(stored)) return stored[memberIndex];
  if (stored && typeof stored === 'object') {
    const named = (stored as Record<string, unknown>)[memberName];
    if (named !== undefined) return named;
  }
  return undefined;
}

/**
 * Default for a tuple member from framework metadata: member `defaultValue` /
 * `value` first, then positional / named slice of the tuple argument's own
 * `defaultValue` / `value` (e.g. RGB on the tuple node).
 */
export function tupleMemberSchemaDefault(tupleArg: FrameworkArgument, memberIndex: number, member: FrameworkArgument): unknown {
  const memberVal = member.defaultValue ?? member.value;
  if (memberVal !== undefined && memberVal !== null && String(memberVal) !== '') {
    return memberVal;
  }
  const tupleDef = tupleArg.defaultValue ?? tupleArg.value;
  if (Array.isArray(tupleDef)) {
    const v = tupleDef[memberIndex];
    if (v !== undefined && v !== null && String(v) !== '') return v;
  }
  if (tupleDef && typeof tupleDef === 'object' && !Array.isArray(tupleDef)) {
    const named = (tupleDef as Record<string, unknown>)[member.name];
    if (named !== undefined && named !== null && String(named) !== '') return named;
  }
  return undefined;
}
